// Чистата логика на „Плащане на доставки": групите по доставчик (така, както
// ги рисува списъкът) и Excel-ът им. Файлът следва екрана ред по ред: за всяка
// група — документите ѝ (най-старият отгоре, както идват от BE), после ред
// „Общо · N документа" със сумата на групата; накрая „Общо" за всички
// неплатени. Периодът няма — изнасят се всички неплатени.
import type { TFunction } from "i18next";
import dayjs, { type Dayjs } from "dayjs";
import type { PayableDelivery } from "../../../api/inventoryDeliveries";
import type { ExcelColumn } from "../../../utils/excelExport";
import { excelMoney } from "../components/list/excelCells";
import { docTypeOf } from "../delivery/components/deliveryMeta";

export type SupplierGroup = { key: string; name: string; docs: PayableDelivery[]; total: number };

// „Без доставчик" най-отдолу, останалите по азбучен ред
export const groupPayableBySupplier = (payable: PayableDelivery[], noSupplierLabel: string): SupplierGroup[] => {
  const byKey = new Map<string, SupplierGroup>();
  for (const doc of payable) {
    const key = String(doc.supplierId ?? "none");
    const group = byKey.get(key) ?? { key, name: doc.supplierName || noSupplierLabel, docs: [], total: 0 };
    group.docs.push(doc);
    group.total += doc.totalValueWithVat || 0;
    byKey.set(key, group);
  }
  return [...byKey.values()].sort((a, b) =>
    a.key === "none" ? 1 : b.key === "none" ? -1 : a.name.localeCompare(b.name, "bg"),
  );
};

// Колко дни документът чака плащане (по календарен ден; днешният = 0)
export const payableAgeDays = (doc: Pick<PayableDelivery, "date">, today: Dayjs = dayjs()) =>
  Math.max(0, today.startOf("day").diff(dayjs(doc.date).startOf("day"), "day"));

export type PaymentExcelRow = {
  supplier: string;
  type: string;
  document: string;
  date: string;
  warehouse: string;
  user: string;
  // null в редовете „Общо"
  ageDays: number | null;
  gross: number;
};

export type PaymentExcelLabels = {
  typeOf: (doc: PayableDelivery) => string;
  noNumber: string;
  // „Общо · 3 документа" (групата) / „3 документа" (всички)
  groupTotal: (count: number) => string;
  docsCount: (count: number) => string;
  total: string;
};

export const paymentExcelRows = (
  groups: SupplierGroup[],
  labels: PaymentExcelLabels,
  today: Dayjs = dayjs(),
): PaymentExcelRow[] => {
  const rows: PaymentExcelRow[] = [];
  let count = 0;
  let total = 0;
  for (const group of groups) {
    for (const doc of group.docs) {
      rows.push({
        supplier: group.name,
        type: labels.typeOf(doc),
        document: doc.invoiceNumber || labels.noNumber,
        date: dayjs(doc.date).format("DD.MM.YYYY"),
        warehouse: doc.warehouseName ?? "",
        user: doc.createdByName ?? "",
        ageDays: payableAgeDays(doc, today),
        gross: excelMoney(doc.totalValueWithVat),
      });
    }
    rows.push({
      supplier: group.name,
      type: "",
      document: labels.groupTotal(group.docs.length),
      date: "",
      warehouse: "",
      user: "",
      ageDays: null,
      gross: excelMoney(group.total),
    });
    count += group.docs.length;
    total += group.total;
  }
  if (!count) return rows;
  rows.push({
    supplier: labels.total,
    type: "",
    document: labels.docsCount(count),
    date: "",
    warehouse: "",
    user: "",
    ageDays: null,
    gross: excelMoney(total),
  });
  return rows;
};

export const paymentExcelLabels = (t: TFunction): PaymentExcelLabels => ({
  typeOf: (doc) =>
    docTypeOf(doc) === "note" ? t("inventoryPage.docTypeNote") : t("inventoryPage.docTypeInvoice"),
  noNumber: t("inventoryPage.noDocNumber"),
  groupTotal: (count) => `${t("inventoryPage.total")} · ${t("inventoryPage.payDocsCount", { count })}`,
  docsCount: (count) => t("inventoryPage.payDocsCount", { count }),
  total: t("inventoryPage.total"),
});

export const paymentExcelColumns = (t: TFunction): ExcelColumn<PaymentExcelRow>[] => [
  { header: t("inventoryPage.colSupplier"), value: (row) => row.supplier, width: 26 },
  { header: t("inventoryPage.colDocType"), value: (row) => row.type },
  { header: t("inventoryPage.colDocument"), value: (row) => row.document, width: 20 },
  { header: t("inventoryPage.colDate"), value: (row) => row.date, width: 12 },
  { header: t("inventoryPage.colWarehouse"), value: (row) => row.warehouse },
  { header: t("inventoryPage.filterEnteredBy"), value: (row) => row.user },
  { header: t("inventoryPage.tbColAgeDays"), value: (row) => row.ageDays },
  { header: t("inventoryPage.colGross"), value: (row) => row.gross },
];
